import React, { useRef, useState, useEffect, useCallback } from 'react';

interface DoubleScrollbarProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const DoubleScrollbar: React.FC<DoubleScrollbarProps> = ({
  children,
  className = '',
  style
}) => {
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);

  const [scrollWidth, setScrollWidth] = useState<number>(0);
  const [showTopScroll, setShowTopScroll] = useState<boolean>(false);
  const isSyncingRef = useRef<boolean>(false);

  const updateWidth = useCallback(() => {
    if (bottomScrollRef.current) {
      const el = bottomScrollRef.current;
      const sw = el.scrollWidth;
      const cw = el.clientWidth;

      setScrollWidth(sw);
      // Show top scrollbar only if content overflows horizontally (desktop/tablet)
      setShowTopScroll(sw > cw + 1);
    }
  }, []);

  useEffect(() => {
    updateWidth();

    const bottomEl = bottomScrollRef.current;
    if (!bottomEl) return;

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(bottomEl);
    if (bottomEl.firstElementChild) {
      observer.observe(bottomEl.firstElementChild);
    }

    window.addEventListener('resize', updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [updateWidth]);

  const handleTopScroll = () => {
    if (isSyncingRef.current || !bottomScrollRef.current || !topScrollRef.current) return;
    isSyncingRef.current = true;
    bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  };

  const handleBottomScroll = () => {
    // Only sync to top scrollbar if top scroll is rendered and visible (e.g. desktop)
    if (isSyncingRef.current || !topScrollRef.current || !bottomScrollRef.current || window.innerWidth < 768) return;
    isSyncingRef.current = true;
    topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  };

  return (
    <div className="w-full">
      {/* Top Scrollbar (Visible on desktop/tablets with horizontal overflow) */}
      {showTopScroll && (
        <div
          ref={topScrollRef}
          onScroll={handleTopScroll}
          className="hidden md:block overflow-x-auto overflow-y-hidden w-full mb-1 overscroll-x-contain"
          style={{ height: '8px' }}
        >
          <div
            style={{
              width: `${scrollWidth}px`,
              height: '1px',
            }}
          />
        </div>
      )}

      {/* Bottom Main Content Container */}
      <div
        ref={bottomScrollRef}
        onScroll={handleBottomScroll}
        className={`overflow-x-auto overscroll-x-contain touch-pan-x ${className}`}
        style={{
          WebkitOverflowScrolling: 'touch',
          ...style
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default DoubleScrollbar;
